import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "components/custom.css";
import useTextToSpeech from 'src/utils/useTextToSpeech';
import WordDefinitionModal from "components/PracticeMaterial/PopUps/WordDefinitionModal";

const SingleAnswer = (props) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [secondCount, setSecondCount] = useState(1200);
  const [selectedWord, setSelectedWord] = useState("");
  const [wordDefinitionModalIsOpen, setWordDefinitionModalIsOpen] = useState(false);
  const { speak } = useTextToSpeech({
    language: "English",
    preferMaleVoice: true,
  });
  // Removed unused states:
  // const [question, setQuestion] = useState(""); // Can be derived from props
  // const [is_answer, setIs_answer] = useState(false); // Not used in rendering
  // const [correctFlag, setCorrectFlag] = useState(true); // Logic moved to useEffect
  // const [resultObj, setResultObj] = useState({}); // Can be calculated on demand
  
  // Refs
  const intervalIdRef = useRef(null);
  const correctAnswerRef = useRef(null);
  
  // Memoized values
  const questionText = useMemo(() => props.data?.question || "", [props.data]);
  const data = props.data;
  const { testMode, resultMode, answer, languages } = props;

  // Calculate correct answer once when data changes
  useEffect(() => {
    if (data?.option) {
      const correctOption = data.option.find(item => item.correct == 1);
      correctAnswerRef.current = correctOption?.id || null;
      
      // Submit correct answer to parent when data changes
      if (props.submit) {
        props.submit({
          answer: "",
          correct: correctAnswerRef.current
        });
      }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle component mount and unmount
  useEffect(() => {
    if (!answer) {
      intervalIdRef.current = setInterval(() => {
        setSecondCount(prev => {
          if (prev <= 1) {
            if (intervalIdRef.current) {
              clearInterval(intervalIdRef.current);
              intervalIdRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [answer]);

  // Handle question changes and resets
  useEffect(() => {
    if (!data) return;
    
    const isNewQuestion = questionId && questionId !== data.id;
    const shouldReset = isNewQuestion || props.restart;
    
    if (shouldReset) {
      // Clear existing interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      
      // Reset state
      setSecondCount(1200);
      setQuestionId(data.id);
      setReset(true);
      setSelectedValue('');
      
      // Restart timer if not in answer mode
      if (!answer) {
        intervalIdRef.current = setInterval(() => {
          setSecondCount(prev => {
            if (prev <= 1) {
              if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      
      // If restart prop is true, call restartQue
      if (props.restart) {
        props.restartQue?.();
      }
    } else if (!questionId) {
      setQuestionId(data.id);
    }
  }, [data, props.restart, questionId, answer]);

  const selectSpan = (e, item) => {
		if (!testMode) {
			// const speech = new Speech();
			// speech.speak({ text: item });
			speak(item);

			openCloseWordDefinitionModal(true);
			setSelectedWord(item);
		}
	}

  const openCloseWordDefinitionModal = (status) => {
		setWordDefinitionModalIsOpen(status);
    if(status == false) setSelectedWord('');
	}

  // Optimized handler for radio button changes
  const handleChange = useCallback((event) => {
    if (props.showResult) {
      props.showResult();
    }
    
    const newValue = event.target.value;
    setSelectedValue(newValue);
    
    // Submit to parent
    if (props.submit) {
      props.submit({
        answer: newValue,
        correct: correctAnswerRef.current
      }, true);
    }
  }, [props.submit, props.showResult]);

  // Handle double click to deselect
  const handleDoubleClick = useCallback((event) => {
    if (!!answer) return; // Don't allow if answer is submitted
    
    if (selectedValue) {
      setSelectedValue('');
      
      // Submit deselected state
      if (props.submit) {
        props.submit({
          answer: '',
          correct: correctAnswerRef.current
        }, true);
      }
    }
  }, [selectedValue, answer, props.submit]);

  // Optimized class name calculation
  const getOptionClassName = useCallback((item) => {
    if (!answer) return "";
    
    const isSelected = selectedValue == item.id;
    const isCorrect = item.correct === 1;
    
    if (isCorrect) {
      return isSelected ? "success-wrapper" : "normal-wrapper";
    } else {
      return isSelected ? "error-wrapper" : "";
    }
  }, [selectedValue, answer]);

  // Memoized options list - reversed as in original
  const optionsList = useMemo(() => {
    if (!data?.option || resultMode) return null;
    
    return [...data.option].reverse().map((item, i) => (
      <label 
        key={`${item.id}-${i}`}
        className={`radio-wrapper ${getOptionClassName(item)}`}
        onDoubleClick={handleDoubleClick}
      >
        <p>{item.options}</p>
        <input
          type="radio"
          name="answer"
          value={item.id}
          checked={selectedValue == item.id}
          onChange={handleChange}
          disabled={!!answer}
          aria-label={`Option: ${item.options}`}
        />
        <span className="checkmark"></span>
      </label>
    ));
  }, [data, resultMode, selectedValue, answer, handleChange, handleDoubleClick, getOptionClassName]);

  // Commented out unused functions:
  
  // const showAnswer = useCallback(() => {
  //   if (props.answer != is_answer) {
  //     setIs_answer(true);
  //   }
  // }, [props.answer, is_answer]);

  // const stopTest = useCallback(() => {
  //   if (props.nextQuestion) {
  //     const resultObj = {
  //       answer: selectedValue,
  //       correct: correctAnswerRef.current
  //     };
  //     props.nextQuestion(resultObj);
  //   }
  // }, [selectedValue, props.nextQuestion]);

  // Commented out unused effects:
  
  // useEffect(() => {
  //   if (props.answer) {
  //     showAnswer();
  //   }
  // }, [props.answer]);

  // useEffect(() => {
  //   if (props.stopRecording) {
  //     stopTest();
  //   }
  // }, [props.stopRecording]);

  return (
    <div className="visibility-visible">
      <div className="custom-box mx-0">
        {/* <p className="gray-text question-text">{questionText}</p> */}
        <p className="gray-text question-text">
          {questionText.split(' ').map(function (item, index) {
            return <React.Fragment key={index}><span className={(testMode) ? "" : "speach-text"} onClick={(e) => selectSpan(e, item)}>{item}</span>&nbsp; </React.Fragment>;
          }, this)}
        </p>
      </div>

      <div className="editor-wrapper-section custom-box mx-0">
        <div className="radiolist-content">
          <h4>{data?.question_mcq}</h4>
          <div className="radiolist-group">
            {optionsList}
          </div>
        </div>
      </div>
      {!testMode && !resultMode &&
        <WordDefinitionModal
          isOpen={wordDefinitionModalIsOpen}
          toggle={openCloseWordDefinitionModal}
          word={selectedWord}
          languages={languages}
        />
      }
    </div>
  );
};

export default SingleAnswer;